/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import { type ChangeEvent } from 'react'

import type { BudgetConfigurationValues } from '../../types'

const defaultValues = {
  name: '',
  amount: '' as unknown as number,
}

const useBudgetSettings = ({
  initialValues,
  onSubmit,
}: {
  initialValues?: BudgetConfigurationValues
  onSubmit: (values: BudgetConfigurationValues) => void
}) => {
  const { formatMessage } = useIntl()

  const _validateFields = (values: BudgetConfigurationValues) => {
    const errors: Partial<Record<keyof BudgetConfigurationValues, string>> = {}

    const { name, amount } = values

    // name error validations
    if (!name?.length) {
      errors.name = formatMessage({
        id: 'billing.notifications.budgets.configuration.budget-name.empty-error',
        defaultMessage: 'Enter a budget name',
      })
    }

    // amount error validations
    if (!amount.toString().length || amount <= 0) {
      errors.amount = formatMessage({
        id: 'billing.notifications.budgets.configuration.target-amount.empty-error',
        defaultMessage: 'Enter a target amount',
      })
    }

    return errors
  }

  const formik = useFormik<BudgetConfigurationValues>({
    initialValues: {
      name: initialValues?.name || defaultValues.name,
      amount: initialValues?.amount || defaultValues.amount,
    },
    validate: _validateFields,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit,
  })

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name: fieldName, value } = e.target

    formik.setFieldValue(fieldName, value)

    // clean errors on the field that is being updated
    if (formik.errors[fieldName as keyof BudgetConfigurationValues]) {
      formik.setFieldError(fieldName, undefined)
    }
  }

  return { ...formik, handleChange }
}

export default useBudgetSettings
